/**
 * Test mode utilities for E2E testing
 * These functions detect and interact with mock data injected by Playwright tests
 */

interface MockWalletState {
  connected: boolean
  address: string | null
  publicKey: string | null
}

interface MockBlockchainState {
  nextResult: 'success' | 'failure' | 'failure-then-success'
  attempts: number
}

declare global {
  interface Window {
    __E2E_TEST_MODE__?: boolean
    __MOCK_WALLET__?: MockWalletState
    __MOCK_BLOCKCHAIN__?: MockBlockchainState
  }
}

/**
 * Check if running in E2E test mode
 */
export const isE2ETestMode = (): boolean => {
  return typeof window !== 'undefined' && window.__E2E_TEST_MODE__ === true
}

/**
 * Get mock wallet state if in test mode
 */
export const getMockWalletState = (): MockWalletState | null => {
  if (typeof window !== 'undefined' && window.__MOCK_WALLET__) {
    return window.__MOCK_WALLET__
  }
  return null
}

/**
 * Get mock blockchain behavior if in test mode
 * Returns the expected result for the next blockchain transaction
 */
export const getMockBlockchainResult = (): { isSuccess: boolean } | null => {
  if (typeof window === 'undefined' || !window.__MOCK_BLOCKCHAIN__) {
    return null
  }

  const mock = window.__MOCK_BLOCKCHAIN__

  if (mock.nextResult === 'success') {
    return { isSuccess: true }
  }

  if (mock.nextResult === 'failure') {
    return { isSuccess: false }
  }

  if (mock.nextResult === 'failure-then-success') {
    mock.attempts = (mock.attempts || 0) + 1
    // First attempt fails, subsequent attempts succeed
    return { isSuccess: mock.attempts > 1 }
  }

  return null
}
